import { Logger, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import maplibregl from "maplibre-gl";
import { MapitUtils } from "src/managers/MapitUtils";
export type OverlayListingItem = {
   key: string;
   url: string;
   size?: number;
   lastModified?: string;
};
export type OverlayPersistenceRecord = {
   s3Key: string;
   url: string;
   coordinates: [number, number][];
   opacity: number;
   fileName: string;
};
type StorageConfig = {
   bucket?: string;
   path: string;
};
export type AspectSource =
   | { blob: Blob; dataUrl?: never }
   | { dataUrl: string; blob?: never };
const FALLBACK_CENTER = { lng: 10.0, lat: 56.0 };
const FALLBACK_DELTA = 0.05;
function getStorageConfig(): StorageConfig {
   const bucket = SettingsManager.getSystemSetting("ann.documentStorageS3Bucket", undefined, true);
   let path = SettingsManager.getSystemSetting("ann.documentStorageS3Path", undefined, true);
   if (!path.endsWith("/")) {
      path = `${path}/`;
   }
   if (path.startsWith("/")) {
      path = path.slice(1);
   }
   return { bucket, path };
}
function buildPublicUrl(bucket: string, key: string) {
   const normalizedKey = key.startsWith("/") ? key.slice(1) : key;
   const overlayPrefix =
      SettingsManager.getSystemSetting("imageOverlayPublicUrlPrefix", undefined, true) ||
      SettingsManager.getSystemSetting("s3bucketUrlPrefix", undefined, true);
   if (overlayPrefix) {
      const trimmedPrefix = overlayPrefix.endsWith("/")
         ? overlayPrefix.slice(0, -1)
         : overlayPrefix;
      return `${trimmedPrefix}/${normalizedKey}`;
   }
   return `https://${bucket}.s3.amazonaws.com/${normalizedKey}`;
}
export function extractS3KeyFromUrl(url: string): string | null {
   try {
      const urlObj = new URL(url);
      const overlayPrefix =
         SettingsManager.getSystemSetting("imageOverlayPublicUrlPrefix", undefined, true) ||
         SettingsManager.getSystemSetting("s3bucketUrlPrefix", undefined, true);
      if (overlayPrefix) {
         const trimmedPrefix = overlayPrefix.endsWith("/")
            ? overlayPrefix.slice(0, -1)
            : overlayPrefix;
         if (url.startsWith(trimmedPrefix)) {
            const key = url.substring(trimmedPrefix.length + 1);
            return key.startsWith("/") ? key.slice(1) : key;
         }
      }
      if (urlObj.hostname.includes(".s3") && urlObj.hostname.includes("amazonaws.com")) {
         const key = urlObj.pathname.startsWith("/") ? urlObj.pathname.slice(1) : urlObj.pathname;
         return key || null;
      }
      if (urlObj.hostname === "s3.amazonaws.com" || urlObj.hostname.includes("s3.")) {
         const pathParts = urlObj.pathname.split("/").filter(p => p);
         if (pathParts.length >= 2) {
            return pathParts.slice(1).join("/");
         }
      }
      return null;
   } catch {
      return null;
   }
}
function getPersistence(bucket?: string) {
   if (!bucket) {
      return null;
   }
   return new ViamapPersistenceLayer(bucket);
}
function guessExtension(file: File) {
   const nameExt = file.name?.split(".").pop();
   if (nameExt && nameExt.length <= 5) {
      return nameExt.toLowerCase();
   }
   const mimeExt = file.type?.split("/").pop();
   if (mimeExt && mimeExt.length <= 5) {
      return mimeExt.toLowerCase();
   }
   return undefined;
}
export async function uploadOverlayImage(file: File): Promise<{ key: string; url: string }> {
   const config = getStorageConfig();
   const persistence = getPersistence(config.bucket);
   if (!config.bucket || !persistence) {
      throw new Error("Image overlay storage is not configured.");
   }
   const extension = guessExtension(file);
   const objectKey = `${config.path}${MapitUtils.getNewUuid()}${extension ? `.${extension}` : ""}`;
   const metadata = {
      filename: encodeURIComponent(file.name || objectKey),
      size: file.size.toFixed(0),
      lastModified: (file.lastModified || Date.now()).toString(),
      "cache-control": "public, max-age=31536000",
      inherit: "true",
      "content-type": file.type || "application/octet-stream",
   };
   await persistence.uploadObjectS3(objectKey, file, metadata);
   const url = buildPublicUrl(config.bucket, objectKey);
   return { key: objectKey, url };
}
export async function listOverlayImages(): Promise<OverlayListingItem[]> {
   const config = getStorageConfig();
   const persistence = getPersistence(config.bucket);
   if (!config.bucket || !persistence) {
      return [];
   }
   const listing = await persistence.getListingS3(config.path);
   return listing
      .filter((item: any) => item?.Key && !item.Key.endsWith("/"))
      .map((item: any) => ({
         key: item.Key,
         url: buildPublicUrl(config.bucket!, item.Key),
         size: item.Size,
         lastModified:
            typeof item.LastModified === "string"
               ? item.LastModified
               : item.LastModified?.toString?.(),
      }));
}
export async function deleteOverlayImage(key: string): Promise<void> {
   const config = getStorageConfig();
   const persistence = getPersistence(config.bucket);
   if (!config.bucket || !persistence) {
      throw new Error("Image overlay storage is not configured.");
   }
   await persistence.deleteObjectS3(key);
}
export async function saveOverlayRecordToEs(record: OverlayPersistenceRecord): Promise<void> {
   Logger.logInfo("ImageOverlay", "saveOverlayRecordToEs", JSON.stringify(record));
}
async function determineImageAspectRatio(source: AspectSource): Promise<number> {
   return new Promise((resolve, reject) => {
      const img = new Image();
      let objectUrl: string | undefined;
      img.onload = () => {
         if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
         }
         const aspect = img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
         resolve(aspect || 1);
      };
      img.onerror = (err) => {
         if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
         }
         reject(err ?? new Error("Failed to determine image aspect ratio"));
      };
      if ("blob" in source && source.blob) {
         objectUrl = URL.createObjectURL(source.blob);
         img.src = objectUrl;
      } else if ("dataUrl" in source && source.dataUrl) {
         img.src = source.dataUrl;
      } else {
         reject(new Error("No image data provided"));
      }
   });
}
export function computeDefaultOverlayCoordinates(
   map?: maplibregl.Map,
   aspectRatio: number = 1
): [number, number][] {
   const targetAspect = Math.max(aspectRatio, 0.01);
   const buildRectangle = (center: maplibregl.LngLatLike, widthDeg: number, heightDeg: number) => {
      const lngLat = maplibregl.LngLat.convert(center);
      const lng = lngLat.lng;
      const lat = lngLat.lat;
      return [
         [lng - widthDeg / 2, Math.min(90, lat + heightDeg / 2)],
         [lng + widthDeg / 2, Math.min(90, lat + heightDeg / 2)],
         [lng + widthDeg / 2, Math.max(-90, lat - heightDeg / 2)],
         [lng - widthDeg / 2, Math.max(-90, lat - heightDeg / 2)],
      ] as [number, number][];
   };
   if (!map) {
      const latFactor = Math.max(Math.cos((FALLBACK_CENTER.lat * Math.PI) / 180), 1e-6);
      const heightDeg = FALLBACK_DELTA;
      const widthDeg = (heightDeg * targetAspect) / latFactor;
      return buildRectangle(FALLBACK_CENTER, widthDeg, heightDeg);
   }
   const bounds = map.getBounds();
   const center = map.getCenter();
   const latFactor = Math.max(Math.cos((center.lat * Math.PI) / 180), 1e-6);
   let widthDeg = Math.max((bounds.getEast() - bounds.getWest()) / 4, 0.001);
   let heightDeg = Math.max((bounds.getNorth() - bounds.getSouth()) / 4, 0.001);
   const currentAspect = (widthDeg * latFactor) / heightDeg;
   if (targetAspect > currentAspect) {
      widthDeg = (targetAspect * heightDeg) / latFactor;
   } else {
      heightDeg = (widthDeg * latFactor) / targetAspect;
   }
   return buildRectangle(center, widthDeg, heightDeg);
}
export async function deriveOverlayPlacement(
   source: AspectSource,
   map?: maplibregl.Map
): Promise<{ aspectRatio: number; coordinates: [number, number][] }> {
   const aspectRatio = await determineImageAspectRatio(source);
   const coordinates = computeDefaultOverlayCoordinates(map, aspectRatio);
   return { aspectRatio, coordinates };
}
export async function downloadOverlayImage(key: string): Promise<{
   blob: Blob;
   contentType: string;
   fileName?: string;
}> {
   const config = getStorageConfig();
   const persistence = getPersistence(config.bucket);
   if (!config.bucket || !persistence) {
      throw new Error("Image overlay storage is not configured.");
   }
   const objectData = await persistence.getObjectS3(key);
   const contentType = objectData.ContentType || "application/octet-stream";
   const bodyBuffer = objectData.Body as Buffer;
   const blob = new Blob([new Uint8Array(bodyBuffer)], { type: contentType });
   return {
      blob,
      contentType,
      fileName: objectData.Metadata?.filename && decodeURIComponent(objectData.Metadata.filename),
   };
}
export function sanitizeFilename(name: string): string {
   return name.replace(/[^a-z0-9_-]+/gi, "_");
}
function extractMimeType(dataUrl: string): string | undefined {
   const match = /^data:(.+?);/.exec(dataUrl);
   return match?.[1];
}
export async function buildFileFromDataUrl(dataUrl: string, baseName: string): Promise<File> {
   const response = await fetch(dataUrl);
   if (!response.ok) {
      throw new Error("Unable to read overlay image data.");
   }
   const blob = await response.blob();
   const mimeType = blob.type || extractMimeType(dataUrl) || "application/octet-stream";
   const extension = (mimeType.split("/").pop() || "png").toLowerCase();
   const safeName = sanitizeFilename(baseName || "overlay");
   return new File([blob], `${safeName}.${extension}`, { type: mimeType });
}
export function blobToDataUrl(blob: Blob): Promise<string> {
   return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read overlay blob."));
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
   });
}
